// src/pages/Tpes.js
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken, getActiveAuthRole } from '../utils/authStorage';
import {
  FaCircle,
  FaSpinner,
  FaSync,
  FaCreditCard,
  FaLink,
  FaToggleOn,
  FaToggleOff,
  FaChevronLeft,
  FaChevronRight,
  FaWifi,
  FaSearch,
  FaTags,
  FaTimes,
  FaCheckCircle,
  FaStore
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_DEVICE_SERVICE_URL;
const API_BASE_DEVICE = process.env.REACT_APP_DEVICE_SERVICE_URL; 

export default function Tpes() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(null);

  // map clientId => { connected: boolean, store: object | id | null }
  const [tpeConnectionStatus, setTpeConnectionStatus] = useState({});
  const [connectionLoading, setConnectionLoading] = useState(false);

  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination state (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Global stats
  const [globalStats, setGlobalStats] = useState({
    totalActive: 0,
    totalInactive: 0,
    totalConnected: 0,
    totalDisconnected: 0
  });

  const itemsPerPage = 8;

  const [draftTpeId, setDraftTpeId] = useState('');
  const [draftStore, setDraftStore] = useState('');
  const [draftConnection, setDraftConnection] = useState('');
  const [appliedTpeId, setAppliedTpeId] = useState('');
  const [appliedStore, setAppliedStore] = useState('');
  const [appliedConnection, setAppliedConnection] = useState('');
  const [storeOptions, setStoreOptions] = useState([{ value: '', label: 'Tous les magasins' }]);

  // --- Helpers auth (token) ---
  const getToken = () => getActiveToken() || null;
  const getAuthHeaders = () => {
    const token = getToken();
    if (!token) return null;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  /** Finance & marchand : pas de colonne Actions (tags / activer-désactiver masqués pour le marchand). */
  const currentRole = getActiveAuthRole();
  const hideFinanceActions = currentRole === 'finance';
  const hideMerchantActions = currentRole === 'merchant';
  const hideActionsColumn = hideFinanceActions || hideMerchantActions;
  const hideManagerTagAction = currentRole === 'manager';

  const FILTER_FETCH_LIMIT = 3000;

  function buildStoreOptions(merged, tpesList) {
    const map = new Map();
    const push = (raw) => {
      if (!raw || typeof raw !== 'object') return;
      const code = raw.code != null ? String(raw.code).trim() : '';
      const name = raw.name != null ? String(raw.name).trim() : '';
      const val = code || name || (raw._id ? String(raw._id) : '');
      if (!val) return;
      const label = code && name && code !== name ? `${code} · ${name}` : code || name || val;
      if (!map.has(val)) map.set(val, label);
    };
    (merged || []).forEach((d) => push(d.storeResolved || (typeof d.storeId === 'object' ? d.storeId : null)));
    (tpesList || []).forEach((t) => push(typeof t.storeId === 'object' ? t.storeId : null));
    const opts = [...map.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'fr'))
      .map(([value, label]) => ({ value, label }));
    return [{ value: '', label: 'Tous les magasins' }, ...opts];
  }

  function deviceMatchesFilters(dev, f) {
    const tid = (f.tpeId || '').trim().toLowerCase();
    if (tid && !String(dev.clientId || '').toLowerCase().includes(tid)) return false;
    const st = (f.store || '').trim().toLowerCase();
    if (st) {
      const res = dev.storeResolved || (typeof dev.storeId === 'object' ? dev.storeId : null);
      const code = (res?.code || '').toLowerCase();
      const name = (res?.name || '').toLowerCase();
      const idStr = String(res?._id || '').toLowerCase();
      if (code !== st && name !== st && idStr !== st && !code.includes(st) && !name.includes(st)) return false;
    }
    if (f.connection === 'connected' && !dev.connected) return false;
    if (f.connection === 'disconnected' && dev.connected) return false;
    return true;
  }

  // --- Récupérer /tpes (list + map) ---
  const fetchTpesList = async () => {
    setConnectionLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        setError('Token manquant');
        setConnectionLoading(false);
        return { list: [], map: {} };
      }

      const res = await fetch(`${API_BASE_DEVICE}/tpes`, { headers, mode: 'cors' });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Impossible de charger le statut TPE');
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : data.devices || [];
      const map = {};
      list.forEach(t => {
        map[t.clientId] = {
          connected: !!t.connected,
          store: t.storeId || null
        };
      });

      setTpeConnectionStatus(map);
      return { list, map };
    } catch (err) {
      console.error('Erreur récupération statut connexion TPE:', err);
      setError(err.message || 'Erreur récupération statut TPE');
      return { list: [], map: {} };
    } finally {
      setConnectionLoading(false);
    }
  };

  const fetchAvailableTags = async () => {
    setTagsLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await fetch(`${API_BASE}/tags?active=true`, { headers, mode: 'cors' });
      if (!res.ok) throw new Error('Impossible de charger les tags');
      const data = await res.json();
      setAvailableTags(Array.isArray(data?.tags) ? data.tags : []);
    } catch (err) {
      console.warn('Erreur récupération tags:', err);
      setAvailableTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  // 📊 Récupérer stats globales (utilise la map de connection pour calculer connected/disconnected)
  const fetchGlobalStats = async (connectionMap = {}) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        setError('Token manquant');
        return;
      }

      const res = await fetch(`${API_BASE}/devices?clientType=TPE&limit=1000`, { headers, mode: 'cors' });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Erreur récupération stats');
      }

      const data = await res.json();
      const allDevices = Array.isArray(data.devices) ? data.devices : (Array.isArray(data) ? data : []);
      const activeCount = allDevices.filter(device => device.status === 'active').length;
      const inactiveCount = allDevices.filter(device => device.status === 'inactive').length;

      const connectedCount = allDevices.filter(device => connectionMap[device.clientId]?.connected === true).length;
      const disconnectedCount = allDevices.filter(device => connectionMap[device.clientId]?.connected === false).length;

      setGlobalStats({
        totalActive: activeCount,
        totalInactive: inactiveCount,
        totalConnected: connectedCount,
        totalDisconnected: disconnectedCount
      });
    } catch (err) {
      console.error('Erreur récupération statistiques TPE:', err);
      setError(err.message || 'Erreur récupération statistiques');
      setGlobalStats({
        totalActive: 0,
        totalInactive: 0,
        totalConnected: 0,
        totalDisconnected: 0
      });
    }
  };

  // fetch devices filtered for TPE with pagination and merge with /tpes data
  const mergeDeviceList = (list, connectionMap) =>
    list.map((dev) => {
      const conn = connectionMap[dev.clientId];
      const storeFromConn = conn && conn.store ? conn.store : null;
      const finalStore =
        dev.storeId && typeof dev.storeId === 'object' && (dev.storeId.code || dev.storeId.name)
          ? dev.storeId
          : storeFromConn || dev.storeId || null;
      return {
        ...dev,
        connected: conn ? !!conn.connected : false,
        storeResolved: finalStore,
      };
    });

  const fetchDevices = async (page = 1, filtersOverride = null) => {
    setLoading(true);
    setError(null);

    const filters = filtersOverride ?? {
      tpeId: appliedTpeId,
      store: appliedStore,
      connection: appliedConnection,
    };
    const filtersActive = Boolean(
      filters.tpeId.trim() || filters.store.trim() || filters.connection,
    );

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        setError('Token manquant');
        setLoading(false);
        return;
      }

      const { list: tpesList, map: connectionMap } = await fetchTpesList();

      const limitParam = filtersActive ? FILTER_FETCH_LIMIT : itemsPerPage;
      const pageParam = filtersActive ? 1 : page;

      let res;
      try {
        res = await fetch(
          `${API_BASE}/devices?clientType=TPE&includePairing=true&page=${pageParam}&limit=${limitParam}`,
          { headers, mode: 'cors' },
        );
      } catch (err) {
        console.warn('Erreur fetch /devices:', err);
        res = null;
      }

      let data = null;
      if (res && res.ok) {
        try {
          data = await res.json();
        } catch (err) {
          console.warn('Impossible de parser /devices JSON:', err);
          data = null;
        }
      } else if (res && !res.ok) {
        const t = await res.text();
        console.warn('/devices responded with error:', t);
      }

      const list = data && Array.isArray(data.devices) ? data.devices : Array.isArray(data) ? data : [];

      let merged = [];
      const fromDevicesApi = list && list.length > 0;

      if (fromDevicesApi) {
        merged = mergeDeviceList(list, connectionMap);
      } else if (tpesList && tpesList.length > 0) {
        merged = tpesList.map((t) => ({
          clientId: t.clientId,
          _id: t._id || null,
          status: t.status || 'unknown',
          pairing: t.pairing || null,
          lastSeen: t.lastSeen || null,
          storeId: t.storeId || null,
          storeResolved: t.storeId || null,
          connected: !!t.connected,
        }));
      }

      setStoreOptions(buildStoreOptions(merged, tpesList));

      if (merged.length === 0) {
        setDevices([]);
        setTotalDevices(0);
        setTotalPages(0);
        setCurrentPage(1);
        setGlobalStats({ totalActive: 0, totalInactive: 0, totalConnected: 0, totalDisconnected: 0 });
        await fetchGlobalStats(connectionMap);
        return;
      }

      const filtered = merged.filter((d) => deviceMatchesFilters(d, filters));

      if (filtersActive) {
        const total = filtered.length;
        const totalPgs = Math.max(1, Math.ceil(total / itemsPerPage));
        const safePage = Math.min(Math.max(1, page), totalPgs);
        const slice = filtered.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);
        setDevices(slice);
        setTotalDevices(total);
        setTotalPages(totalPgs);
        setCurrentPage(safePage);
      } else if (fromDevicesApi) {
        setDevices(merged);
        setTotalDevices(data.total || merged.length);
        setTotalPages(data.totalPages || 0);
        setCurrentPage(data.page || page);
      } else {
        setDevices(merged);
        setTotalDevices(merged.length);
        setTotalPages(1);
        setCurrentPage(1);
        const totalConnected = tpesList.filter((t) => !!t.connected).length;
        const totalDisconnected = tpesList.filter((t) => t.connected === false).length;
        setGlobalStats((prev) => ({
          ...prev,
          totalConnected,
          totalDisconnected,
        }));
      }

      await fetchGlobalStats(connectionMap);
    } catch (err) {
      console.error('Erreur fetchDevices:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const next = {
      tpeId: draftTpeId.trim(),
      store: draftStore,
      connection: draftConnection,
    };
    setAppliedTpeId(next.tpeId);
    setAppliedStore(next.store);
    setAppliedConnection(next.connection);
    fetchDevices(1, next);
  };

  const handleResetFilters = () => {
    setDraftTpeId('');
    setDraftStore('');
    setDraftConnection('');
    setAppliedTpeId('');
    setAppliedStore('');
    setAppliedConnection('');
    fetchDevices(1, { tpeId: '', store: '', connection: '' });
  };

  useEffect(() => {
    fetchDevices(1);
    fetchAvailableTags();
  }, []);

  // Auto-refresh du statut de connexion (période courte)
  useEffect(() => {
    const connectionRefreshInterval = setInterval(() => {
      if (!connectionLoading) {
        const silentConnectionRefresh = async () => {
          setIsAutoRefreshing(true);
          try {
            const headers = getAuthHeaders();
            if (!headers) return;

            const res = await fetch(`${API_BASE_DEVICE}/tpes`, { headers, mode: 'cors' });
            if (res.ok) {
              const data = await res.json();
              const list = Array.isArray(data) ? data : data.devices || [];
              const map = {};
              list.forEach(t => {
                map[t.clientId] = { connected: !!t.connected, store: t.storeId || null };
              });

              setTpeConnectionStatus(map);

              // update devices' connected and storeResolved fields
              setDevices(prev => prev.map(dev => {
                const conn = map[dev.clientId];
                const storeFromConn = conn && conn.store ? conn.store : null;
                const finalStore = (dev.storeId && typeof dev.storeId === 'object' && (dev.storeId.code || dev.storeId.name))
                  ? dev.storeId
                  : storeFromConn || dev.storeId || null;
                return { ...dev, connected: !!(conn && conn.connected), storeResolved: finalStore };
              }));

              // update global stats for current page devices (use latest prev devices snapshot)
              setGlobalStats(prev => {
                const connectedCount = Object.values(map).filter(v => v.connected === true).length;
                const disconnectedCount = Object.values(map).filter(v => v.connected === false).length;
                return { ...prev, totalConnected: connectedCount, totalDisconnected: disconnectedCount };
              });
            }
          } catch (err) {
            console.warn('Auto-refresh connexion: Erreur mise à jour statut connexion:', err);
          } finally {
            setIsAutoRefreshing(false);
          }
        };

        silentConnectionRefresh();
      }
    }, 2000);

    return () => {
      clearInterval(connectionRefreshInterval);
    };
  }, [connectionLoading]);

  // toggle status (handles being passed either clientId or _id)
  const toggleStatus = async (idOrClientId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setIsUpdating(idOrClientId);
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Token manquant');
        setIsUpdating(null);
        return;
      }

      // try to find device to get _id; otherwise fallback to what was passed
      const device = devices.find(d => d._id === idOrClientId || d.clientId === idOrClientId) || {};
      const idToCall = device._id || idOrClientId;

      const res = await fetch(`${API_BASE}/devices/${idToCall}/status?status=${newStatus}`, { method: 'PUT', headers, mode: 'cors' });
      const resultText = await res.text();
      let result;
      try { result = JSON.parse(resultText); } catch { result = { message: resultText }; }
      if (!res.ok) throw new Error(result.message || 'Erreur mise à jour');

      // update devices state by matching either clientId or _id
      setDevices(prev => prev.map(dev =>
        (dev.clientId === idOrClientId || dev._id === idOrClientId) ? { ...dev, status: newStatus } : dev
      ));

      setGlobalStats(prev => ({
        ...prev,
        totalActive: newStatus === 'active' ? prev.totalActive + 1 : prev.totalActive - 1,
        totalInactive: newStatus === 'inactive' ? prev.totalInactive + 1 : prev.totalInactive - 1
      }));
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    } finally {
      setIsUpdating(null);
    }
  };
  const formatDateSimple = (timestamp) => {
    if (!timestamp) return '—';
    // Parse ISO string directly without timezone conversion
    const match = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return '—';
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  const getStatusBadge = (status) =>
    status === 'active' ? (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
        <FaCircle className="mr-1 h-2 w-2" />Actif
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200/70">
        <FaCircle className="mr-1 h-2 w-2" />Inactif
      </span>
    );

  const getConnectionBadge = (clientId) => {
    const info = tpeConnectionStatus[clientId];

    if (connectionLoading) {
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
          <FaSpinner className="mr-1 h-2 w-2 animate-spin" />
          Vérification…
        </span>
      );
    }

    if (!info) {
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
          <FaCircle className="mr-1 h-2 w-2" />
          Inconnu
        </span>
      );
    }

    if (info.connected === true) {
      return (
        <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
          <FaWifi className="mr-1 h-2 w-2" />
          Connecté
        </span>
      );
    }

    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200/70">
        <FaCircle className="mr-1 h-2 w-2" />
        Déconnecté
      </span>
    );
  };

  const refreshData = () => {
    fetchDevices(1);
    fetchAvailableTags();
  };

  const openTagsModal = (device) => {
    const currentTagIds = Array.isArray(device?.tagIds)
      ? device.tagIds.map((id) => String(id))
      : [];
    setSelectedDevice(device);
    setSelectedTagIds(currentTagIds);
    setShowTagsModal(true);
  };

  const closeTagsModal = () => {
    if (savingTags) return;
    setShowTagsModal(false);
    setSelectedDevice(null);
    setSelectedTagIds([]);
  };

  const toggleTagSelection = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const saveDeviceTags = async () => {
    if (!selectedDevice) return;
    setSavingTags(true);
    setError(null);
    setSuccessMessage('');
    try {
      const headers = getAuthHeaders();
      if (!headers) throw new Error('Token manquant');

      const clientId = selectedDevice.clientId || selectedDevice._id;
      const res = await fetch(`${API_BASE}/devices/${clientId}`, {
        method: 'PUT',
        headers,
        mode: 'cors',
        body: JSON.stringify({ tagIds: selectedTagIds })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || 'Erreur lors de la sauvegarde des tags');
      }

      const syncedTagIds = Array.isArray(data?.tagIds) ? data.tagIds.map((id) => String(id)) : selectedTagIds;
      setDevices((prev) =>
        prev.map((dev) =>
          (dev.clientId === clientId || dev._id === clientId)
            ? { ...dev, tagIds: syncedTagIds }
            : dev
        )
      );

      setSuccessMessage('Tags affectés avec succès');
      closeTagsModal();
    } catch (err) {
      setError(err.message || 'Erreur mise à jour tags');
    } finally {
      setSavingTags(false);
    }
  };

  const renderTagBadges = (tagIds = []) => {
    if (!Array.isArray(tagIds) || tagIds.length === 0) {
      return <span className="text-gray-400 text-sm">Aucun tag</span>;
    }
    const names = tagIds
      .map((id) => availableTags.find((tag) => String(tag._id) === String(id))?.name)
      .filter(Boolean);

    if (names.length === 0) {
      return <span className="text-gray-500 text-xs">{tagIds.length} tag(s)</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {names.slice(0, 2).map((name) => (
          <span key={name} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200/60">
            {name}
          </span>
        ))}
        {names.length > 2 && (
          <span className="text-xs text-gray-500">+{names.length - 2}</span>
        )}
      </div>
    );
  };

  const renderTagsDropdown = (tagIds = []) => {
    if (!Array.isArray(tagIds) || tagIds.length === 0) {
      return (
        <select
          disabled
          className="px-2 py-1 border rounded text-xs bg-gray-100 text-gray-500"
        >
          <option>Aucun tag</option>
        </select>
      );
    }

    const names = tagIds
      .map((id) => availableTags.find((tag) => String(tag._id) === String(id))?.name)
      .filter(Boolean);

    if (names.length === 0) {
      return (
        <select
          disabled
          className="px-2 py-1 border rounded text-xs bg-gray-100 text-gray-500"
        >
          <option>{tagIds.length} tag(s)</option>
        </select>
      );
    }

    if (names.length === 1) {
      return (
        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200/60">
          {names[0]}
        </span>
      );
    }

    return (
      <select
        value=""
        onChange={(e) => {
          e.target.value = '';
        }}
        className="max-w-[180px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/15"
      >
        <option value="" disabled>{names.length} tag(s)</option>
        {names.map((name, idx) => (
          <option key={`${name}-${idx}`} value={name} disabled>{name}</option>
        ))}
      </select>
    );
  };

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchDevices(newPage);
    }
  };

  // Mobile TPE Card Component (affiche store code si disponible)
  const TpeCard = ({ tpe }) => {
    const store = tpe.storeResolved || (tpe.storeId && typeof tpe.storeId === 'object' ? tpe.storeId : null);
    const storeLabel = store ? (store.code || store.name || '—') : '—';
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <FaCreditCard className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="truncate font-mono text-sm font-semibold text-slate-900">{tpe.clientId || tpe._id}</div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                <FaStore className="h-3 w-3 shrink-0 text-slate-400" />
                <span className="truncate font-medium text-slate-700">{storeLabel}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Dernière connexion : {formatDateSimple(tpe.lastSeen)}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {getStatusBadge(tpe.status)}
            {getConnectionBadge(tpe.clientId)}
          </div>
        </div>

        <div className="mb-3 grid grid-cols-1 gap-3 text-sm">
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Tags</span>
            <div className="font-medium">{renderTagBadges(tpe.tagIds)}</div>
          </div>
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Appairage</span>
            <div className="font-medium">
              {tpe.pairing && tpe.pairing.paired ? (
                <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
                  <FaLink className="mr-1" />
                  {tpe.pairing.pairedDevice?.clientId}
                </span>
              ) : (
                <span className="text-slate-400">Non appairé</span>
              )}
            </div>
          </div>
        </div>

        {!hideActionsColumn && (
          <div className="flex justify-end border-t border-slate-100 pt-3">
            {!hideManagerTagAction && (
              <button
                onClick={() => openTagsModal(tpe)}
                className="mr-2 inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
              >
                <FaTags className="mr-1" />
                Tags
              </button>
            )}
            <button
              onClick={() => toggleStatus(tpe.clientId || tpe._id, tpe.status)}
              disabled={isUpdating === (tpe.clientId || tpe._id)}
              className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                tpe.status === 'active'
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {isUpdating === (tpe.clientId || tpe._id) ? (
                <FaSpinner className="animate-spin mr-1" />
              ) : tpe.status === 'active' ? (
                <FaToggleOff className="mr-1" />
              ) : (
                <FaToggleOn className="mr-1" />
              )}
              {tpe.status === 'active' ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const tableColSpan = hideActionsColumn ? 7 : 8;
  const hasActiveFilters = !!(appliedTpeId.trim() || appliedStore.trim() || appliedConnection);

  const PaginationComponent = () => {
    if (totalPages <= 1) return null;
    const maxVisible = window.innerWidth < 768 ? 3 : 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const startIdx = ((currentPage - 1) * itemsPerPage) + 1;
    const endIdx = Math.min(currentPage * itemsPerPage, totalDevices);

    return (
      <div className="sticky bottom-0 z-20 flex flex-col items-center justify-between gap-2 border-t border-slate-200/90 bg-white/95 px-4 py-3 backdrop-blur-sm sm:flex-row">
        <div className="order-2 text-sm text-slate-500 sm:order-1">
          Affichage de {totalDevices === 0 ? 0 : startIdx} à {totalDevices === 0 ? 0 : endIdx} sur {totalDevices}
        </div>
        <div className="order-1 flex items-center gap-1 sm:order-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          {pages.map(p => (
            <button
              key={p}
              onClick={() => handlePageChange(p)}
              className={`min-w-[2rem] rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${p === currentPage ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="bg-white border-b shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <FaCreditCard className="text-white text-sm" />
                </div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">TPE</h1>
                {isAutoRefreshing && (
                  <div className="flex items-center text-xs text-gray-500">
                    <FaSync className="animate-spin mr-1" />
                    <span className="hidden sm:inline">Actualisation...</span>
                  </div>
                )}
                {successMessage && (
                  <div className="flex items-center text-xs text-emerald-600 ml-2">
                    <FaCheckCircle className="mr-1" />
                    {successMessage}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={refreshData}
                  disabled={loading}
                  className="flex items-center px-3 py-2 border rounded-lg text-xs font-medium hover:bg-gray-50"
                >
                  <FaSync className={`${loading ? 'animate-spin' : 'group-hover:rotate-180'} mr-1.5 text-xs`} />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaCreditCard className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total TPE</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{totalDevices}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{globalStats.totalActive}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-violet-200/70 bg-violet-50/50 px-2.5 py-2 shadow-sm ring-1 ring-violet-900/5">
                <FaWifi className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-violet-800/90">Connectés</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-violet-950">{globalStats.totalConnected}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-amber-200/70 bg-amber-50/40 px-2.5 py-2 shadow-sm ring-1 ring-amber-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-amber-800/90">Déconnectés</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-amber-950">{globalStats.totalDisconnected}</p>
                </div>
              </div>
            </div>

            <div className="mt-3 border-t border-slate-200/90 pt-3">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Recherche & filtres</p>
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <div className="min-w-[120px] flex-1 sm:max-w-[180px]">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">TPE ID</label>
                  <div className="relative mt-0.5">
                    <FaSearch className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={draftTpeId}
                      onChange={(e) => setDraftTpeId(e.target.value)}
                      placeholder="Ex. TPE-001…"
                      className="block w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
                <div className="min-w-[100px] sm:w-40">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Magasin</label>
                  <select
                    value={draftStore}
                    onChange={(e) => setDraftStore(e.target.value)}
                    className="mt-0.5 block w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {storeOptions.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[100px] sm:w-36">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Connexion</label>
                  <select
                    value={draftConnection}
                    onChange={(e) => setDraftConnection(e.target.value)}
                    className="mt-0.5 block w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Tous</option>
                    <option value="connected">Connecté</option>
                    <option value="disconnected">Non connecté</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={handleApplyFilters}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                  >
                    Appliquer
                  </button>
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          {loading && (
            <div className="flex h-64 items-center justify-center px-4">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                <p className="text-sm text-gray-600">Chargement des TPE...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center sm:p-6">
              <FaCreditCard className="mx-auto mb-2 text-2xl text-red-500" />
              <p className="text-sm font-medium text-red-700">Erreur : {error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5">
              <div className="hidden min-h-0 flex-1 md:block">
                <div className="h-full overflow-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">TPE ID</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Magasin</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Connexion</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Tags</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Appairage</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Statut</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Dernière connexion</th>
                        {!hideActionsColumn && (
                          <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {devices.length === 0 ? (
                        <tr>
                          <td colSpan={tableColSpan} className="px-6 py-14 text-center">
                            <div className="mx-auto flex max-w-sm flex-col items-center text-slate-500">
                              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                <FaCreditCard className="text-2xl text-slate-400" />
                              </div>
                              <p className="text-sm font-semibold text-slate-700">Aucun TPE trouvé</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {hasActiveFilters ? 'Modifiez vos critères de recherche.' : 'Aucun terminal enregistré pour le moment.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        devices.map((dev, idx) => {
                          const store = dev.storeResolved || (dev.storeId && typeof dev.storeId === 'object' ? dev.storeId : null);
                          const storeLabel = store ? (store.code || store.name || '—') : '—';
                          return (
                            <tr key={dev.clientId || dev._id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                              <td className="px-5 py-4 align-top whitespace-nowrap">
                                <div className="flex items-start gap-3">
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                    <FaCreditCard className="h-3.5 w-3.5" />
                                  </div>
                                  <div className="min-w-0 font-mono text-sm font-semibold text-slate-900">
                                    {dev.clientId || dev._id}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap text-sm text-slate-700">
                                {storeLabel !== '—' ? (
                                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs font-semibold text-slate-700">
                                    <FaStore className="h-3 w-3 text-slate-400" />
                                    {storeLabel}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {getConnectionBadge(dev.clientId)}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {renderTagsDropdown(dev.tagIds)}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {dev.pairing && dev.pairing.paired ? (
                                  <span className="inline-flex max-w-[10rem] items-center truncate rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
                                    <FaLink className="mr-1 shrink-0" />
                                    <span className="truncate">{dev.pairing.pairedDevice?.clientId}</span>
                                  </span>
                                ) : (
                                  <span className="text-sm text-slate-400">Non appairé</span>
                                )}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {getStatusBadge(dev.status)}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap text-sm tabular-nums text-slate-500">
                                {formatDateSimple(dev.lastSeen)}
                              </td>
                              {!hideActionsColumn && (
                                <td className="px-4 py-4 align-top">
                                  <div className="flex flex-wrap gap-1.5">
                                    {!hideManagerTagAction && (
                                      <button
                                        onClick={() => openTagsModal(dev)}
                                        className="inline-flex items-center rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                                      >
                                        <FaTags className="mr-1" />
                                        Tags
                                      </button>
                                    )}
                                    <button
                                      onClick={() => toggleStatus(dev.clientId || dev._id, dev.status)}
                                      disabled={isUpdating === (dev.clientId || dev._id)}
                                      className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                                        dev.status === 'active'
                                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                      }`}
                                    >
                                      {isUpdating === (dev.clientId || dev._id) ? (
                                        <FaSpinner className="mr-1 animate-spin" />
                                      ) : dev.status === 'active' ? (
                                        <FaToggleOff className="mr-1" />
                                      ) : (
                                        <FaToggleOn className="mr-1" />
                                      )}
                                      {dev.status === 'active' ? 'Désactiver' : 'Activer'}
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-4 md:hidden">
                {devices.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FaCreditCard className="text-2xl text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">Aucun TPE trouvé</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {devices.map(dev => <TpeCard key={dev.clientId || dev._id} tpe={dev} />)}
                  </div>
                )}
              </div>

              <PaginationComponent />
            </div>
          )}
        </main>

        {showTagsModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeTagsModal}
          >
            <div
              className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Affecter des tags - {selectedDevice?.clientId || selectedDevice?._id}
                </h2>
                <button onClick={closeTagsModal} className="text-gray-400 hover:text-gray-600 p-2">
                  <FaTimes />
                </button>
              </div>

              <div className="max-h-72 overflow-auto border rounded-lg p-3">
                {tagsLoading ? (
                  <div className="text-sm text-gray-500 flex items-center">
                    <FaSpinner className="animate-spin mr-2" />
                    Chargement des tags...
                  </div>
                ) : availableTags.length === 0 ? (
                  <div className="text-sm text-gray-500">Aucun tag actif disponible</div>
                ) : (
                  <div className="space-y-2">
                    {availableTags.map((tag) => {
                      const id = String(tag._id);
                      const checked = selectedTagIds.includes(id);
                      return (
                        <label key={id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{tag.name}</div>
                            <div className="text-xs text-gray-500">{tag.slug || '—'}</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTagSelection(id)}
                            className="w-4 h-4"
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeTagsModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveDeviceTags}
                  disabled={savingTags}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm disabled:opacity-60"
                >
                  {savingTags ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
